'use client';

import { CoordinatedTeam } from 'bff';
import { useTranslations } from 'i11n';
import { ChevronDown, Users } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'ui';
import { fmtNumber } from './formatters';

interface Props {
  teams: CoordinatedTeam[];
  // undefined = "all my teams" (the initial state).
  value?: string;
  onChange: (teamId?: string) => void;
  // Distinct people across the whole scope, from the server: NOT the sum of the
  // per-team counts, which would double-count a coordinator who is a member of
  // several of their teams.
  allTeamsMemberCount?: number;
  hint?: string;
}

// The ONLY team filter of the "Performance del equipo" dashboard: it drives every
// block of all three tabs. Always rendered, even for a coordinator with a single
// team, so the scope the numbers describe is never implicit.
export const ScopeSelector = ({
  teams,
  value,
  onChange,
  allTeamsMemberCount,
  hint,
}: Props) => {
  const t = useTranslations();

  const selectedTeam = useMemo(
    () => teams.find((team) => team.id === value),
    [teams, value],
  );

  const selectAllTeams = useCallback(() => onChange(undefined), [onChange]);

  const label = selectedTeam
    ? selectedTeam.name
    : t('metrics.team-management.scope.all-teams');

  // With every team selected the button carries a badge naming them, so "Todos
  // mis equipos" is never ambiguous about what it covers.
  const allTeamNames = useMemo(
    () => teams.map((team) => team.name).join(' + '),
    [teams],
  );

  const membersLabel = useCallback(
    (count: number) =>
      t('metrics.team-management.scope.members', { count: fmtNumber(count) }),
    [t],
  );

  return (
    <div className='flex flex-wrap items-center gap-3'>
      <span className='text-xs font-medium uppercase tracking-wider text-muted-foreground'>
        {t('metrics.team-management.scope.label')}
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' className='max-w-full gap-2 font-medium'>
            <Users className='h-3.5 w-3.5 shrink-0' />
            <span className='truncate'>{label}</span>
            {!selectedTeam && teams.length > 1 && (
              <Badge
                variant='secondary'
                className='hidden max-w-64 font-normal sm:inline-flex'
              >
                <span className='truncate'>{allTeamNames}</span>
              </Badge>
            )}
            <ChevronDown className='h-3.5 w-3.5 shrink-0' />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align='start'
          className='max-h-[60vh] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto'
        >
          <DropdownMenuItem
            onClick={selectAllTeams}
            className='flex items-center justify-between gap-4'
          >
            <div className='min-w-0'>
              <div className='font-medium'>
                {t('metrics.team-management.scope.all-teams')}
              </div>
              <div className='line-clamp-2 text-xs text-muted-foreground'>
                {teams.map((team) => team.name).join(' · ')}
              </div>
            </div>
            {allTeamsMemberCount !== undefined && (
              <Badge variant='secondary' className='shrink-0 font-normal'>
                {membersLabel(allTeamsMemberCount)}
              </Badge>
            )}
          </DropdownMenuItem>

          {teams.map((team) => (
            <DropdownMenuItem
              key={team.id}
              onClick={() => onChange(team.id)}
              className='flex items-center justify-between gap-4'
            >
              <span className='truncate font-medium'>{team.name}</span>
              <Badge variant='secondary' className='shrink-0 font-normal'>
                {membersLabel(team.member_count)}
              </Badge>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <span className='text-sm text-muted-foreground'>
        {hint ?? t('metrics.team-management.scope.hint')}
      </span>
    </div>
  );
};
